import { isNil } from 'lodash';
import { z } from 'zod';

export const PRICING_STRATEGY_VALIDATION_PROMPT_NAME =
  'Pricing Strategy Instructions Validation';

export const PricingStrategyValidationSchema = z.object({
  isValid: z.boolean(),
  issues: z.array(
    z.object({
      rule: z.string(),
      explanation: z.string(),
    })
  ),
});
export type PricingStrategyValidationResult = z.infer<
  typeof PricingStrategyValidationSchema
>;

export enum PricingStrategyType {
  ACTION_DEFINITION = 'ACTION_DEFINITION',
  SIMPLE_MARGIN = 'SIMPLE_MARGIN',
  MILEAGE_BASED = 'MILEAGE_BASED',
  MILEAGE_RATE = 'MILEAGE_RATE',
  WEIGHTED_AVERAGE = 'WEIGHTED_AVERAGE',
}

export enum RateComponentType {
  LINE_HAUL = 'LINE_HAUL',
  FUEL_SURCHARGE = 'FUEL_SURCHARGE',
  MANUAL_ADJUSTMENT = 'MANUAL_ADJUSTMENT',
  MARGIN = 'MARGIN',
  TMS_ADJUSTMENT = 'TMS_ADJUSTMENT',
  MESSAGE_ADJUSTMENT = 'MESSAGE_ADJUSTMENT',
  TOTAL = 'TOTAL',
  OTHER = 'OTHER',
}

const ZodDATRatingMethod = z.enum(['FORECAST', 'HISTORY', 'RATE']);
const ZodGreenscreensRatingMethod = z.enum(['DEFAULT', 'NETWORK']);
const ZodFastmoreRatingMethod = z.enum(['SOLO', 'TEAM']);
const ZodTruckstopRatingMethod = z.enum(['BOOKED', 'POSTED']);
const IntegrationSpecificRatingMethod = ZodDATRatingMethod.or(
  ZodGreenscreensRatingMethod
)
  .or(ZodFastmoreRatingMethod)
  .or(ZodTruckstopRatingMethod);

export enum RatingMethod {
  ARCBEST_TL_QUOTING = 'ARCBEST_TL_QUOTING',
  DAT = 'DAT',
  DRAYRATES_RATING = 'DRAYRATES_RATING',
  DTS_RATING = 'DTS_RATING',
  ECHO = 'ECHO',
  GREENSCREENS = 'GREENSCREENS',
  HUMAN_INPUT = 'HUMAN_INPUT',
  CUSTOM = 'CUSTOM',
  SUNSET = 'SUNSET',
  NFI = 'NFI',
  NTG = 'NTG',
  TABI = 'TABI',
  WERNER = 'WERNER',
  TRANSFIX = 'TRANSFIX',
  BITFREIGHTER = 'BITFREIGHTER',
  WWEX = 'WWEX',
  DIRECT_OFFER = 'DIRECT_OFFER',
  FASTMORE = 'FASTMORE',
  FREIGHTPLUS = 'FREIGHTPLUS',
  WORLDWIDE_LOGISTICS = 'WORLDWIDE_LOGISTICS',
  AVRL = 'AVRL',
  TRUCKSTOP = 'TRUCKSTOP',
  SONAR = 'SONAR',
  SOFTMODAL = 'SOFTMODAL',
}

export const PricingStrategyConfigurationBaseSchema = z.object({});
export type PricingStrategyConfigurationBase = z.infer<
  typeof PricingStrategyConfigurationBaseSchema
>;

export const SimpleLLMPricingCalculatorConfigSchema =
  PricingStrategyConfigurationBaseSchema.extend({
    llmLineItemPrompt: z.string().optional().nullable(),
  });
export type SimpleLLMPricingCalculatorConfig = z.infer<
  typeof SimpleLLMPricingCalculatorConfigSchema
>;

// RatingMethodEntry Schema
export const RatingMethodEntrySchema = z.object({
  ratingMethod: z.nativeEnum(RatingMethod),
  integrationSpecificRatingMethod:
    IntegrationSpecificRatingMethod.optional().nullable(),
  weightedAveragePercentage: z
    .number()
    .int()
    .min(1)
    .max(99)
    .optional()
    .nullable(),
});
export type RatingMethodEntry = z.infer<typeof RatingMethodEntrySchema>;

// Shared validation: when 2+ rating methods exist, all must have weightedAveragePercentage summing to 100
const validateRatingMethodWeights = (
  data: { ratingMethods: Array<{ weightedAveragePercentage?: number | null }> },
  ctx: z.RefinementCtx
): void => {
  if (data.ratingMethods.length < 2) return;
  const allHavePercentage = data.ratingMethods.every(
    (m) => !isNil(m.weightedAveragePercentage)
  );
  if (!allHavePercentage) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message:
        'weightedAveragePercentage is required for all entries when there are 2 or more rating methods',
      path: ['ratingMethods'],
    });
    return;
  }
  const total = data.ratingMethods.reduce(
    (sum, m) => sum + (m.weightedAveragePercentage ?? 0),
    0
  );
  if (total !== 100) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'weightedAveragePercentage values must sum to 100',
      path: ['ratingMethods'],
    });
  }
};

// Internal base object (no refinements) — kept as ZodObject so downstream schemas can .merge()/.extend()
const CarrierRateBasedPricingCalculatorConfigObjectSchema =
  SimpleLLMPricingCalculatorConfigSchema.extend({
    ratingMethods: z.array(RatingMethodEntrySchema).min(1),
    minimumMargin: z.number().optional().nullable(),
    maximumMargin: z.number().optional().nullable(),
  });

// CarrierRateBasedPricingCalculatorConfig Schema (with validation)
export const CarrierRateBasedPricingCalculatorConfigSchema =
  CarrierRateBasedPricingCalculatorConfigObjectSchema.superRefine(
    validateRatingMethodWeights
  );
export type CarrierRateBasedPricingCalculatorConfig = z.infer<
  typeof CarrierRateBasedPricingCalculatorConfigSchema
>;

const ensureZodMarginOrMarkup = (data: {
  marginPercentage?: number | null;
  markupDollars?: number | null;
}) => isNil(data.marginPercentage) !== isNil(data.markupDollars);

const ensureZodMarginOrMarkupMessage = {
  message:
    "Exactly one of 'marginPercentage' or 'markupDollars' must be provided",
  path: ['marginPercentage', 'markupDollars'],
};

// MarginMarkupConfig Schema
export const MarginMarkupConfigSchema = z.object({
  marginPercentage: z.number().optional().nullable(),
  markupDollars: z.number().optional().nullable(),
});
export type MarginMarkupConfig = z.infer<typeof MarginMarkupConfigSchema>;

// SimpleMarginConfig Schema
export const SimpleMarginConfigSchema =
  CarrierRateBasedPricingCalculatorConfigObjectSchema.merge(
    MarginMarkupConfigSchema
  )
    .strict()
    .refine(ensureZodMarginOrMarkup, ensureZodMarginOrMarkupMessage)
    .superRefine(validateRatingMethodWeights);
export type SimpleMarginConfig = z.infer<typeof SimpleMarginConfigSchema>;

// MileageMargin Schema
export const MileageMarginSchema = MarginMarkupConfigSchema.extend({
  gtMiles: z.number(),
  lteMiles: z.number().optional().nullable(),
})
  .strict()
  .refine(ensureZodMarginOrMarkup, ensureZodMarginOrMarkupMessage);
export type MileageMargin = z.infer<typeof MileageMarginSchema>;

export const MileageRateSchema = z
  .object({
    perMileRateCents: z.number(),
    gtMiles: z.number(),
    lteMiles: z.number().optional().nullable(),
  })
  .strict();
export type MileageRate = z.infer<typeof MileageRateSchema>;

// MileageBasedConfig Schema
export const MileageBasedConfigSchema =
  CarrierRateBasedPricingCalculatorConfigObjectSchema.extend({
    mileageMargins: z.array(MileageMarginSchema),
  })
    .strict()
    .superRefine(validateRatingMethodWeights);
export type MileageBasedConfig = z.infer<typeof MileageBasedConfigSchema>;

// MileageRateConfig Schema
export const MileageRateConfigSchema =
  SimpleLLMPricingCalculatorConfigSchema.extend({
    mileageRates: z.array(MileageRateSchema),
    minimumMargin: z.number().optional().nullable(),
    maximumMargin: z.number().optional().nullable(),
  })
    .merge(MarginMarkupConfigSchema)
    .strict()
    .refine(ensureZodMarginOrMarkup, ensureZodMarginOrMarkupMessage);
export type MileageRateConfig = z.infer<typeof MileageRateConfigSchema>;

export const ActionDefinitionPricingConfigSchema = z
  .object({
    actionDefinitionId: z.string().min(1),
    ratingMethods: z.array(RatingMethodEntrySchema).min(1),
  })
  .strict()
  .superRefine(validateRatingMethodWeights);
export type ActionDefinitionPricingConfig = z.infer<
  typeof ActionDefinitionPricingConfigSchema
>;

export const NonWeightedAveragePricingStrategyConfigurationSchema = z.union([
  ActionDefinitionPricingConfigSchema,
  SimpleMarginConfigSchema,
  MileageBasedConfigSchema,
  MileageRateConfigSchema,
]);
export type NonWeightedAveragePricingStrategyConfiguration = z.infer<
  typeof NonWeightedAveragePricingStrategyConfigurationSchema
>;

// WeightedAverageComponent Schema
const WeightedAverageComponentSchema = z.discriminatedUnion('type', [
  z.object({
    type: z.literal(PricingStrategyType.ACTION_DEFINITION),
    weight: z.number().int().min(0).max(100),
    config: ActionDefinitionPricingConfigSchema,
  }),

  z.object({
    type: z.literal(PricingStrategyType.SIMPLE_MARGIN),
    weight: z.number().int().min(0).max(100),
    config: SimpleMarginConfigSchema,
  }),

  z.object({
    type: z.literal(PricingStrategyType.MILEAGE_BASED),
    weight: z.number().int().min(0).max(100),
    config: MileageBasedConfigSchema,
  }),

  z.object({
    type: z.literal(PricingStrategyType.MILEAGE_RATE),
    weight: z.number().int().min(0).max(100),
    config: MileageRateConfigSchema,
  }),
]);
export type WeightedAverageComponent = z.infer<
  typeof WeightedAverageComponentSchema
>;

// WeightedAverageConfig Schema
export const WeightedAverageConfigSchema = z
  .object({
    components: z.array(WeightedAverageComponentSchema).min(2),
  })
  .refine(
    (data) => data.components.reduce((sum, c) => sum + c.weight, 0) === 100,
    { message: 'Component weights must sum to 100', path: ['components'] }
  );
export type WeightedAverageConfig = z.infer<typeof WeightedAverageConfigSchema>;

// PricingStrategyConfiguration Schema
export const PricingStrategyConfigurationSchema = z.union([
  ActionDefinitionPricingConfigSchema,
  SimpleMarginConfigSchema,
  MileageBasedConfigSchema,
  MileageRateConfigSchema,
  WeightedAverageConfigSchema,
]);
export type PricingStrategyConfiguration = z.infer<
  typeof PricingStrategyConfigurationSchema
>;

export function pricingStrategyConfigSchemaForType(type: PricingStrategyType) {
  switch (type) {
    case PricingStrategyType.ACTION_DEFINITION:
      return ActionDefinitionPricingConfigSchema;
    case PricingStrategyType.SIMPLE_MARGIN:
      return SimpleMarginConfigSchema;
    case PricingStrategyType.MILEAGE_BASED:
      return MileageBasedConfigSchema;
    case PricingStrategyType.MILEAGE_RATE:
      return MileageRateConfigSchema;
    case PricingStrategyType.WEIGHTED_AVERAGE:
      return WeightedAverageConfigSchema;
    default:
      throw new Error(`Unknown pricing strategy type: ${type}`);
  }
}

export function isWeightedAverageConfig(
  config: PricingStrategyConfiguration
): config is WeightedAverageConfig {
  return 'components' in config;
}

export function extractLlmLineItemPrompts(
  config: PricingStrategyConfiguration
): string[] {
  const configs = isWeightedAverageConfig(config)
    ? config.components.map((component) => component.config)
    : [config];

  return configs
    .map((c) => (c as { llmLineItemPrompt?: string | null }).llmLineItemPrompt)
    .filter((prompt): prompt is string => !!prompt);
}
